import { Box, Button, Divider, Link, Stack, Typography, useTheme } from "@mui/material";
import { Plus } from "@phosphor-icons/react";
import React, { useEffect, useCallback } from "react";
import AddLogDialog from "./AddLogDialog";
import { useGetInfiniteClientLogs } from "hooks/useGetInfiniteClientLogs";
import { useGlobalToast } from "components/GlobalToastProvider";
import LogsFilter from "./LogsFilter";
import { filtersMap, filtersMenuItems, getLogIcon, getLogText, getOpenConversation } from "./Utils";
import { ANLoadingScreen, ANNoResultsOverlay, ANNoDataOverlay } from "@answersnow/pkg-theme";
import noLogsSvg from "../../../../assets/no_logs.svg";
import LogElement from "./LogElement";
import EventDetailsPanel from "./EventDetailsPanel";
import { useSelector, useDispatch } from "react-redux";
import * as selectors from "selectors";
import { useHistory } from "react-router-dom";
import { getObjectChanges } from "utils/getObjectChanges";
import { clearLogs, splitDataChanges } from "./EventDetailsPanel/Utils";
import actions from "actions";

// Constants for toolbar heights
const TOOLBAR_HEIGHT_DESKTOP = "163px";
const TOOLBAR_HEIGHT_MOBILE = "171px";

// Helper function to process log changes
const processLogChanges = (log) => {
  let changes = {};
  const logWithProfileChanges = [log];

  if (log.before && log.after) {
    changes = getObjectChanges(log.after, log.before, [
      "updated_by",
      "updated_at",
      "modified_by",
      "created_by",
      "login",
      "data",
      "id",
      "client_id",
      "user_id",
      "created_at",
    ]);

    if (Object.keys(changes).length === 0) return null;

    const existingLog = logWithProfileChanges.find((l) => l === log);

    if (existingLog) {
      // Merge new changes into the existing log entry
      existingLog.data_changes = existingLog.data_changes || {};

      Object.keys(changes).forEach((key) => {
        existingLog.data_changes[key] = {
          new_value: changes[key].new,
          old: changes[key].old,
        };
      });
    } else {
      // If log is not already in the list, add it as a new entry
      logWithProfileChanges.push({
        ...log,
        data_changes: Object.keys(changes).reduce((acc, key) => {
          acc[key] = { new_value: changes[key].new, old: changes[key].old };
          return acc;
        }, {}),
      });
    }
  }

  return logWithProfileChanges;
};

// Helper function to get log author
const getLogAuthor = (log) => {
  if (log.type !== "document_signatures" || log.document_type === "treatment plan") {
    return log.author;
  }
  return "";
};

// Helper function to check if event details link should be shown
const shouldShowEventDetailsLink = (log) => {
  return (
    ["rescheduled", "canceled", "scheduled"].includes(log?.event_action?.toLowerCase()) &&
    log.before &&
    Object.keys(log.before).length > 1
  );
};

// Component for rendering individual log items
const LogItem = ({
  log,
  showDivider,
  clientName,
  documentTypes,
  clinicianList,
  theme,
  history,
  onOpenDetailsPanel,
}) => {
  const showEventDetailsLink = shouldShowEventDetailsLink(log);
  const author = getLogAuthor(log);

  return (
    <LogElement
      logNote={log.note}
      logAuthor={author}
      showDivider={showDivider}
      openDetailsPanel={onOpenDetailsPanel}
      logDate={new Date(log.activity_date || log.created)}
      icon={getLogIcon(theme, log)}
      logTitle={getLogText(clientName, documentTypes, log, clinicianList)}
      showEventDetailsLink={log?.event_action?.toLowerCase() === "rescheduled"}
      showLogWithNoTitle={log.type === "COMMENT"}
      endTitleElement={
        <>
          {getOpenConversation(history, log)}
          {showEventDetailsLink && (
            <>
              {" "}
              (
              <Link
                underline="hover"
                onClick={() => {
                  onOpenDetailsPanel(log);
                }}
              >
                event details
              </Link>
              )
            </>
          )}
        </>
      }
    />
  );
};

export default function Logs() {
  const dispatch = useDispatch();

  // Get all required data from Redux store
  const customerDetails = useSelector(selectors.getCustomerDetails);
  const clientId = useSelector(selectors.getCustomerClientId);
  const clinicianList = useSelector(selectors.getCliniciansList);
  const documentTypes = useSelector(selectors.getDocumentTypes);

  const childName = customerDetails?.details?.name || "";
  const childLastName = customerDetails?.details?.child_last_name || "";
  const clientName = `${childName} ${childLastName}`;

  const theme = useTheme();
  const history = useHistory();
  const [addLogDialogOpen, setAddLogDialogOpen] = React.useState(false);
  // Tracks the changes in logs count to show toast
  const [originalLogsCount, setOriginalLogsCount] = React.useState(null);
  // Tracks the initial logs count to pass to the endpoint, to offset the logs accordingly
  // to the difference in logs count
  const [initialLogsCount, setInitialLogsCount] = React.useState(null);
  const [filterBy, setFilterBy] = React.useState([]);
  const [isEventDetailsPanelOpen, setIsEventDetailsPanelOpen] = React.useState(false);
  const [sidePanelLog, setSidePanelLog] = React.useState(null);
  const { showToast } = useGlobalToast();
  const {
    data: response,
    isLoading,
    fetchNextPage,
    hasNextPage,
    refetch,
    error,
  } = useGetInfiniteClientLogs(clientId, {
    size: 10,
    prevLogCount: initialLogsCount,
    filters: filterBy.flatMap((logType) => filtersMap[logType]).join(","),
  });
  const hasLogs = response?.pages?.[0]?.data?.logs?.length > 0;

  // Memoized callback for handling filter changes
  const handleFilterChange = useCallback((value) => {
    setOriginalLogsCount(null);
    setInitialLogsCount(null);
    setFilterBy(value);
  }, []);

  // Memoized callback for clearing filters
  const handleClearFilters = useCallback(() => {
    setOriginalLogsCount(null);
    setInitialLogsCount(null);
    setFilterBy([]);
  }, []);

  // Memoized callback for updating logs
  const handleUpdateLogs = useCallback(() => {
    setOriginalLogsCount(null);
    setInitialLogsCount(null);
  }, []);

  // Memoized callback for opening details panel
  const handleOpenDetailsPanel = useCallback((log) => {
    setIsEventDetailsPanelOpen(true);
    if (log) {
      setSidePanelLog(log);
    }
  }, []);

  useEffect(() => {
    const logsCount = response?.pages?.[response.pages.length - 1].data?.logsCount;
    if (logsCount && originalLogsCount === null) {
      setOriginalLogsCount(logsCount);
      setInitialLogsCount(logsCount);
    } else if (logsCount && originalLogsCount !== logsCount) {
      showToast({
        message: "New updates are available.",
        errorState: true,
        retryHandler: () => {
          // Reset tracking logsCount values
          setOriginalLogsCount(null);
          setInitialLogsCount(null);
        },
        errorButtonText: "Refresh this page",
      });
      setOriginalLogsCount(logsCount);
    }
  }, [response, originalLogsCount, showToast]);

  // Update logs if initialLogsCount is reseted
  useEffect(() => {
    if (!initialLogsCount) refetch();
  }, [initialLogsCount, refetch]);

  useEffect(() => {
    dispatch(actions.setCliniciansPerPage(999));
    dispatch(actions.loadCliniciansList());
  }, [dispatch]);



  return (
    <>
      <AddLogDialog
        open={addLogDialogOpen}
        handleClose={() => setAddLogDialogOpen(false)}
        clientId={clientId}
        updateLogs={handleUpdateLogs}
      />

      <Stack
        direction="row"
        id="logs-container"
        sx={{
          height: `calc(100vh - ${TOOLBAR_HEIGHT_DESKTOP})`,
          [theme.breakpoints.down("md")]: {
            height: `calc(100vh - ${TOOLBAR_HEIGHT_MOBILE})`,
          },
        }}
      >
        <Stack
          padding={theme.spacing(7)}
          alignItems="center"
          flexGrow={1}
          sx={{ overflowY: "auto", paddingTop: 0 }}
        >
          <Stack maxWidth="900px" width="100%" gap={theme.spacing(7)}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <LogsFilter
                filtersMenuItems={filtersMenuItems}
                filterBy={filterBy}
                setFilterBy={handleFilterChange}
              />
              <Button
                color="secondary"
                size="small"
                startIcon={<Plus />}
                onClick={(event) => {
                  event.currentTarget.blur();
                  setAddLogDialogOpen(true);
                }}
                sx={{ marginLeft: "8px", minWidth: "fit-content" }}
              >
                Add Log
              </Button>
            </Stack>
            {error ? (
              <Box py="64px">
                <ANNoDataOverlay
                  imgSrc={noLogsSvg}
                  title="Failed to load logs"
                  description="There was an error loading the logs. Please try again."
                />
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button variant="contained" color="primary" onClick={() => refetch()}>
                    Retry
                  </Button>
                </Box>
              </Box>
            ) : isLoading ? (
              <ANLoadingScreen />
            ) : (
              <Stack>
                {!hasLogs && filterBy.length > 0 && (
                  <Box py="64px">
                    <ANNoResultsOverlay handleClearFilters={handleClearFilters} />
                  </Box>
                )}

                {!hasLogs && filterBy.length === 0 && (
                  <Box py="64px">
                    <ANNoDataOverlay
                      imgSrc={noLogsSvg}
                      title="No logs yet"
                      description="Logs will display here with new activity on the client's account."
                    />
                  </Box>
                )}

                {hasLogs &&
                  response?.pages?.map((group, i) => {
                    return (
                      <React.Fragment key={i}>
                        {i > 0 && i === response.pages.length - 1 && (
                          <Stack
                            direction="row"
                            gap={theme.spacing(3)}
                            height="49px"
                            alignItems="center"
                          >
                            <Typography variant="body2" color="text.secondary">
                              More logs
                            </Typography>
                            <Divider sx={{ flexGrow: 1 }} />
                          </Stack>
                        )}

                        {group?.data?.logs?.map((log, index) => {
                          const logWithProfileChanges = processLogChanges(log);

                          if (!logWithProfileChanges) return null;

                          const logs = splitDataChanges(logWithProfileChanges);
                          const cleanLogs = clearLogs(logs);

                          return cleanLogs.map((log, changeIndex) => {
                            const showDivider = !(
                              i === response.pages.length - 1 &&
                              index === group.data.logs.length - 1 &&
                              changeIndex === logWithProfileChanges.length - 1
                            );

                            return (
                              <LogItem
                                key={changeIndex}
                                log={log}
                                showDivider={showDivider}
                                clientName={clientName}
                                documentTypes={documentTypes}
                                clinicianList={clinicianList}
                                theme={theme}
                                history={history}
                                onOpenDetailsPanel={handleOpenDetailsPanel}
                              />
                            );
                          });
                        })}
                      </React.Fragment>
                    );
                  })}
              </Stack>
            )}
            {!isLoading && hasNextPage && (
              <Link onClick={() => fetchNextPage()} underline="hover" sx={{ width: "fit-content" }}>
                View older logs
              </Link>
            )}
          </Stack>
        </Stack>
        <EventDetailsPanel
          open={isEventDetailsPanelOpen}
          setOpen={setIsEventDetailsPanelOpen}
          clientId={clientId}
          parentLog={sidePanelLog}
          clientName={clientName}
        />
      </Stack>
    </>
  );
}
